/**
 * PlayerFinancialsTabView — pure presentation layer for the Financials tab.
 *
 * Renders the document-type dropdown (Contract / Transfer Agreement), the
 * inner tab strip (Contract Tracking | Live Tracking + "Soon" badge), the
 * read-only extraction surface (now delegated to the shared
 * `ContractExtractionView` atom — SE-4333 PR C), the empty state, and the
 * OWNER-gated "Edit in Contracts" button. All state lives in the
 * container — the view is side-effect free so it stays cheap to snapshot
 * and reason about visually.
 *
 * The Live Tracking inner tab renders the same `ClauseTrackingPlaceholder`
 * the desktop drawer uses inside its Live Tracking tab, so the two
 * surfaces are byte-identical without duplicated JSX.
 *
 * PRD 3.3 is enforced at the view layer: `readOnly={true}`,
 * `showDiscardButton={false}`, `showAddDocumentAction={false}` are pinned
 * literals (not wired to any prop) so a future refactor can't let
 * mutation affordances leak into the Player Page.
 * @module features/player-detail/components/PlayerFinancialsTab/PlayerFinancialsTabView
 */

import { ChevronDownIcon } from "lucide-react-native";
import { memo } from "react";

import { Box } from "@/components/atoms/Box";
import { Button } from "@/components/atoms/Button";
import { Stack } from "@/components/atoms/Stack";
import { Pressable } from "@/components/atoms/Pressable";
import {
  Select,
  SelectBackdrop,
  SelectContent,
  SelectDragIndicator,
  SelectDragIndicatorWrapper,
  SelectIcon,
  SelectInput,
  SelectItem,
  SelectPortal,
  SelectTrigger,
} from "@/components/atoms/Select";
import { Text } from "@/components/atoms/Text";
import LabelPill from "@/components/molecules/LabelPill";

import { TEST_ID_PLAYER_DETAIL } from "e2e/testId";
import ClauseTrackingPlaceholder from "@/features/contracts/components/molecules/ClauseTrackingPlaceholder";
import ContractExtractionView from "@/features/contracts/components/organisms/ContractExtractionView";
import type {
  ContractDocumentType,
  ContractExtraction,
} from "@/features/contracts/types";

/** Inner tab keys — keep separate from the outer PlayerPageTab union. */
export type FinancialsInnerTab = "contracts" | "liveTracking";

/** Props accepted by the Financials tab view. */
export interface PlayerFinancialsTabViewProps {
  /** UUID of the player whose data is being displayed. */
  readonly playerId: string;
  /** Whether the current user is an OWNER (drives Edit button visibility). */
  readonly isOwner: boolean;
  /** Currently selected document type used by the extraction query. */
  readonly documentType: ContractDocumentType;
  /** Currently selected inner tab. */
  readonly innerTab: FinancialsInnerTab;
  /** Latest extraction result, or `null` when none exists. */
  readonly extraction: ContractExtraction | null;
  /**
   * Raw Select onValueChange receiver. The container pre-binds the
   * enum cast so the view doesn't have to do any logic itself.
   */
  readonly onDocumentTypeChange: (next: string) => void;
  /** Handler for the "Contract Tracking" inner tab press. */
  readonly onPressContractsTab: () => void;
  /** Handler for the "Live Tracking" inner tab press. */
  readonly onPressLiveTrackingTab: () => void;
  /** Edit-in-contracts press handler. */
  readonly onEditInContractsPress: () => void;
}

/**
 * Renders the Financials tab UI.
 * @param props - View props
 * @param props.playerId - Player UUID whose data is rendered
 * @param props.isOwner - Whether the current user is an OWNER
 * @param props.documentType - Currently selected document type
 * @param props.innerTab - Currently selected inner tab
 * @param props.extraction - Latest extraction (or null when missing)
 * @param props.onDocumentTypeChange - Fired when the user picks a new document type
 * @param props.onPressContractsTab - Fired when the user selects the Contracts inner tab
 * @param props.onPressLiveTrackingTab - Fired when the user selects the Live Tracking inner tab
 * @param props.onEditInContractsPress - Fired when the user clicks Edit in Contracts
 * @returns The tab layout with dropdown, inner tabs, and extraction body
 */
const PlayerFinancialsTabView = ({
  isOwner,
  documentType,
  innerTab,
  extraction,
  playerId,
  onDocumentTypeChange,
  onPressContractsTab,
  onPressLiveTrackingTab,
  onEditInContractsPress,
}: PlayerFinancialsTabViewProps) => (
  <Stack className="w-full gap-4 p-4">
    {/* Document type dropdown + OWNER-only edit button */}
    <Stack
      direction="horizontal"
      className="w-full flex-wrap items-center justify-between gap-3"
    >
      <Box className="min-w-56">
        <Select
          selectedValue={documentType}
          onValueChange={onDocumentTypeChange}
        >
          <SelectTrigger
            testID={TEST_ID_PLAYER_DETAIL.FINANCIALS_DOCUMENT_TYPE_SELECT}
            variant="outline"
            size="md"
          >
            <SelectInput
              placeholder="Document type"
              value={
                documentType === "CONTRACT" ? "Contract" : "Transfer Agreement"
              }
            />
            <SelectIcon as={ChevronDownIcon} />
          </SelectTrigger>
          <SelectPortal>
            <SelectBackdrop />
            <SelectContent>
              <SelectDragIndicatorWrapper>
                <SelectDragIndicator />
              </SelectDragIndicatorWrapper>
              <SelectItem label="Contract" value="CONTRACT" />
              <SelectItem
                label="Transfer Agreement"
                value="TRANSFER_AGREEMENT"
              />
            </SelectContent>
          </SelectPortal>
        </Select>
      </Box>

      {isOwner ? (
        <Button
          testID={TEST_ID_PLAYER_DETAIL.FINANCIALS_EDIT_IN_CONTRACTS_BUTTON}
          variant="outline"
          tone="secondary"
          size="sm"
          onPress={onEditInContractsPress}
          label="Edit in Contracts"
        />
      ) : null}
    </Stack>

    {/* Inner tab strip: Contracts | Live Tracking (with Soon badge) */}
    <Stack
      direction="horizontal"
      className="w-full gap-6 border-b border-outline-default"
    >
      <Pressable
        testID={TEST_ID_PLAYER_DETAIL.FINANCIALS_INNER_TAB_CONTRACT}
        onPress={onPressContractsTab}
      >
        <Stack className="relative pb-3">
          <Text
            variant="body-strong"
            className={`${
              innerTab === "contracts"
                ? "text-content-primary"
                : "text-content-muted"
            }`}
          >
            Contract Tracking
          </Text>
          {innerTab === "contracts" ? (
            <Stack className="absolute inset-x-0 -bottom-px h-micro bg-needs-semantic-primary-500" />
          ) : null}
        </Stack>
      </Pressable>

      <Pressable
        testID={TEST_ID_PLAYER_DETAIL.FINANCIALS_INNER_TAB_LIVE}
        onPress={onPressLiveTrackingTab}
      >
        <Stack className="relative pb-3">
          <Stack direction="horizontal" className="items-center gap-2">
            <Text
              variant="body-strong"
              className={`${
                innerTab === "liveTracking"
                  ? "text-content-primary"
                  : "text-content-muted"
              }`}
            >
              Live Tracking
            </Text>
            <LabelPill label="Soon" tone="muted" />
          </Stack>
          {innerTab === "liveTracking" ? (
            <Stack className="absolute inset-x-0 -bottom-px h-micro bg-needs-semantic-primary-500" />
          ) : null}
        </Stack>
      </Pressable>
    </Stack>

    {/*
      Contracts inner tab. Delegates the saved-extraction render to the
      shared `ContractExtractionView` atom — the same atom the desktop
      drawer and the mobile contract route render. `readOnly` is pinned
      `true` and both mutation-affordance slots are pinned `false` so
      PRD 3.3 ("This view is read-only — editing is not possible from
      the Player Page.") is enforced at the view layer, independent of
      the atom's own OWNER coercion.
    */}
    {innerTab === "contracts" && extraction ? (
      <Box className="w-full">
        <ContractExtractionView
          extraction={extraction}
          playerId={playerId}
          documentType={extraction.documentType}
          readOnly
          showDiscardButton={false}
          showAddDocumentAction={false}
        />
      </Box>
    ) : null}

    {innerTab === "contracts" && !extraction ? (
      <Box
        testID={TEST_ID_PLAYER_DETAIL.FINANCIALS_EMPTY_STATE}
        className="w-full items-center justify-center py-12"
      >
        <Text variant="unstyled" className="text-content-muted">
          No information available.
        </Text>
      </Box>
    ) : null}

    {/*
      Live Tracking inner tab. Renders the same `ClauseTrackingPlaceholder`
      atom the drawer's Live Tracking tab uses so the Player Page and the
      drawer stay byte-identical here — swapping placeholder copy in one
      place updates both surfaces.
    */}
    {innerTab === "liveTracking" ? (
      <Box className="w-full">
        <ClauseTrackingPlaceholder />
      </Box>
    ) : null}
  </Stack>
);

PlayerFinancialsTabView.displayName = "PlayerFinancialsTabView";

export default memo(PlayerFinancialsTabView);
